/**
 * # Button
 *
 * @example
 *
 * new Button(el, { });
 *
 *
 * @module components/button.js
 */

import BaseComponent from './base';
import addClass from '../helpers/dom/add-class';
import removeClass from '../helpers/dom/remove-class';


class Button extends BaseComponent {

  /**
   * Button Constructor
   * @param {Element} el          root element to attach the button
   * @param {Object} [params={}]  button parameters see defaults
   */
  constructor(el, params = {}) {
    super(el, params);
    this._bindEventListenerCallbacks();
  }


  /**
   * Bind listener call backs.
   */
  _bindEventListenerCallbacks() {
    this._onAnimationCompleteBound = this._onAnimationComplete.bind(this);
    this._resetBound = this._reset.bind(this);
  }


  /**
   * Set up references.
   * @param {Element} el base DOM element
   */
  _cacheElements(el) {
    this.el = el;
  }


  /**
   * Return active state.
   * @return {Boolean}
   */
  getActive() {
    return this._active;
  }


  /**
   *  Set the active state of the Button.
   *  @param {Boolean} activeState
   */
  setActive(activeState) {
    this._resetBound();
    this._active = activeState;
    if (this._active === true) {
      addClass(this.el, 'spark-btn--active');
    }
  }


  /**
   * When animation finishes clean up and call onComplete.
   * @param {TransitionEvent} _evt
   */
  _onAnimationComplete(_evt) {
    this._resetBound();
    this.onComplete(_evt);
  }


  /**
   * Reset animation classes and remove animation listener.
   */
  _reset() {
    this.el.querySelector('.spark-btn__label').removeEventListener('transitionend', this._onAnimationCompleteBound);
    removeClass(this.el, 'spark-btn--active', 'spark-btn--complete');
  }


  /**
   * Action was successful, complete animation cycle
   */
  success() {
    if (this._active === true) {
      this._active = false;
      addClass(this.el, 'spark-btn--complete');
      this.el.querySelector('.spark-btn__label').addEventListener('transitionend', this._onAnimationCompleteBound);
    }
  }


  /**
   * Action failed reset the button
   */
  fail() {
    if (this._active === true) {
      this._active = false;
      this._onAnimationCompleteBound();
    }
  }
}


/**
 * Whitelisted parameters which can be set on construction.
 * @type {Array}
 */
Button.prototype._whitelistedParams = [
  'onComplete'
];


/**
 * Default values for internal properties we will be setting.
 * These are set on each construction so we don't leak properties
 * into the prototype chain.
 * @type {Object}
 */
Button.prototype.defaults = {
  el: null,
  _active: false,
  onComplete: () => {}
};


export default Button;
